import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Pencil, StickyNote, Check, X } from "lucide-react";
import { REASON_MAX } from "./ModReasonDialog";

/**
 * The ban/suspension note shown in moderation lists, editable inline.
 * `onSave(reason)` must return a promise.
 */
export default function ModReasonNote({ reason, onSave, testId }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(reason || "");
  const [busy, setBusy] = useState(false);

  const start = () => { setDraft(reason || ""); setEditing(true); };

  const save = async () => {
    setBusy(true);
    try {
      await onSave(draft.trim());
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div className="mt-2 space-y-1.5" data-testid={`${testId}-editor`}>
        <Textarea
          value={draft}
          maxLength={REASON_MAX}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Why was this account actioned?"
          data-testid={`${testId}-input`}
          className="bg-[#0A0A0A] border-[#27272A] text-xs text-[#F3F4F6] placeholder:text-[#4B5563] min-h-[60px] resize-none focus-visible:ring-[#2563EB]"
        />
        <div className="flex items-center justify-end gap-1">
          <span className="text-[10px] text-[#6B7280] mr-auto">{draft.length}/{REASON_MAX}</span>
          <Button
            variant="ghost"
            onClick={() => setEditing(false)}
            data-testid={`${testId}-cancel`}
            className="h-7 px-2 rounded-full text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#1E1E1E] text-[11px]"
          >
            <X className="h-3 w-3 mr-1" /> Cancel
          </Button>
          <Button
            onClick={save}
            disabled={busy}
            data-testid={`${testId}-save`}
            className="h-7 px-3 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[11px]"
          >
            {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />} Save
          </Button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      data-testid={testId}
      className="mt-2 w-full text-left rounded-xl bg-[#0A0A0A] border border-[#1E1E1E] px-2.5 py-2 group hover:border-[#3B82F6]/50 transition-colors"
    >
      <div className="flex items-start gap-2">
        <StickyNote className="h-3 w-3 text-[#6B7280] mt-0.5 shrink-0" />
        <span className={`text-[11px] flex-1 whitespace-pre-wrap break-words ${reason ? "text-[#D1D5DB]" : "text-[#4B5563] italic"}`}>
          {reason || "No reason noted — click to add one"}
        </span>
        <Pencil className="h-3 w-3 text-[#4B5563] group-hover:text-[#3B82F6] shrink-0" />
      </div>
    </button>
  );
}
